import type React from 'react'
import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import {
  type UnitOfMeasure,
  VALIDATION_ERRORS,
  parseValueWithUnit,
  parsedDisplayDecimalPlaces,
} from '@/lib/units/unit-conversion'
import { isShapeOutsidePasteboardBounds } from '@/lib/groups/pasteboard-bounds'
import { notifyObjectKeptWithinPasteboard } from '@/components/app/editor/toasts'
import { canvasMinForSingleShapePanel } from '@/lib/shapes/shape-panel-bounds'
import { mergeFreshShapes } from '../helpers/fresh-shapes'
import { createHistoryTxnId } from '../helpers/history-txn-id'
import { applyMultiSelectPanelMove } from '../helpers/multi-select-commit'
import {
  buildPanelMoveUndoMeta,
  positionRevertPoints,
  toCanvasPosition,
  toDisplayPosition,
} from '../helpers/panel-position'
import { isPanelShapeGeometryBlocked, shapesByIdFromManager } from '../helpers/selection-protection'
import type {
  DisplayPrecision,
  FormatForDisplay,
  PanelFieldErrors,
  PositionField,
  PositionValues,
} from '../types/panel-fields'
import type { CommitBatchOrPersist, PersistShapesFromManager } from '../types/shape-commit'

type SetState<T> = React.Dispatch<React.SetStateAction<T>>

/** X/Y fields: typing, and committing a typed value as a move of the selection. */
export function usePositionField(params: {
  activeObject: ShapeData | null
  activeObjects: ShapeData[]
  isMultiSelect: boolean
  unitOfMeasure: UnitOfMeasure
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  setActiveObject: (obj: ShapeData | null) => void
  setActiveObjects: (objs: ShapeData[]) => void
  formatForDisplay: FormatForDisplay
  setPosition: SetState<PositionValues>
  setFieldErrors: SetState<PanelFieldErrors>
  setDisplayPrecision: SetState<DisplayPrecision>
  persistShapesFromManager: PersistShapesFromManager
  commitBatchOrPersist: CommitBatchOrPersist
  useSecondPageOrigin: boolean
  rightPageOriginX: number
  rightPageOriginY: number
  bleedWidth?: number
  bleedHeight?: number
}) {
  const {
    activeObject,
    activeObjects,
    isMultiSelect,
    unitOfMeasure,
    shapeManagerRef,
    setActiveObject,
    setActiveObjects,
    formatForDisplay,
    setPosition,
    setFieldErrors,
    setDisplayPrecision,
    persistShapesFromManager,
    commitBatchOrPersist,
    useSecondPageOrigin,
    rightPageOriginX,
    rightPageOriginY,
    bleedWidth,
    bleedHeight,
  } = params

  const handlePositionChange = (field: PositionField, value: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
    setPosition((prev) => ({ ...prev, [field]: value }))
  }

  const commitPosition = useCallback(
    (field: PositionField, value: string) => {
      const trimmed = value.trim()
      const mgr = shapeManagerRef?.current
      const origin = { useSecondPageOrigin, rightPageOriginX, rightPageOriginY }
      const revertPosition = () => {
        const currentDisplay = positionRevertPoints({
          field,
          isMultiSelect,
          activeObjects,
          activeObject,
          mgr,
          origin,
        })

        setPosition((prev) => ({
          ...prev,
          [field]: formatForDisplay(currentDisplay),
        }))
      }

      // Empty input: silently restore previous value; object does not move (AC).
      if (trimmed === '') {
        revertPosition()
        setFieldErrors((prev) => ({ ...prev, [field]: VALIDATION_ERRORS.CANT_BE_BLANK }))
        return
      }

      const parsed = parseValueWithUnit(value, unitOfMeasure)

      if (parsed.error) {
        setFieldErrors((prev) => ({ ...prev, [field]: parsed.error }))
        revertPosition()
        return
      }

      const { points } = parsed
      const spreadPoints = toCanvasPosition(points, field, origin)

      if (isMultiSelect && activeObjects.length > 0 && mgr) {
        const historyTxnId = createHistoryTxnId('mixed-move-panel')
        const { batchItems, freshShapes } = applyMultiSelectPanelMove({
          mgr,
          activeObjects,
          field,
          spreadPoints,
        })

        commitBatchOrPersist(batchItems, 'move', 'move', historyTxnId)
        setActiveObjects(mergeFreshShapes(activeObjects, freshShapes))
      } else if (activeObject && mgr) {
        const shapesByIdForCommit = shapesByIdFromManager(mgr)
        const latest = shapesByIdForCommit.get(activeObject.id) ?? activeObject

        if (isPanelShapeGeometryBlocked(latest, shapesByIdForCommit)) {
          return
        }

        const { minX, minY } = canvasMinForSingleShapePanel(activeObject)
        const beforePos = { x: activeObject.x, y: activeObject.y }
        const delta = field === 'x' ? spreadPoints - minX : spreadPoints - minY
        const candidateShape: ShapeData = {
          ...activeObject,
          x: field === 'x' ? activeObject.x + delta : activeObject.x,
          y: field === 'y' ? activeObject.y + delta : activeObject.y,
        }

        // Same pasteboard-bounds check as home.tsx's post-commit revert guard (canvas drag), run
        // proactively here so a typed value never touches the manager in the first place. Shows
        // both the inline field error and the same toast the canvas-drag revert shows, so the
        // two paths give identical feedback.
        if (
          typeof bleedWidth === 'number' &&
          typeof bleedHeight === 'number' &&
          isShapeOutsidePasteboardBounds(candidateShape, bleedWidth, bleedHeight)
        ) {
          setFieldErrors((prev) => ({ ...prev, [field]: VALIDATION_ERRORS.OUT_OF_RANGE_POSITION }))
          const currentDisplay = toDisplayPosition(field === 'x' ? minX : minY, field, origin)

          setPosition((prev) => ({ ...prev, [field]: formatForDisplay(currentDisplay) }))
          notifyObjectKeptWithinPasteboard()
          return
        }

        mgr.updateShape(
          activeObject.id,
          field === 'x' ? { x: candidateShape.x } : { y: candidateShape.y }
        )

        const fresh = mgr.getShapes().find((s) => s.id === activeObject.id)

        if (fresh) setActiveObject(fresh)

        const undoMeta = buildPanelMoveUndoMeta(field, beforePos, fresh)

        if (undoMeta) {
          persistShapesFromManager([activeObject.id], 'move', undoMeta)
        }
      }

      setPosition((prev) => ({
        ...prev,
        [field]: formatForDisplay(points, field, parsedDisplayDecimalPlaces(parsed, unitOfMeasure)),
      }))
      setDisplayPrecision((prev) => ({
        ...prev,
        [field]: parsedDisplayDecimalPlaces(parsed, unitOfMeasure),
      }))
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
    },
    [
      activeObject,
      activeObjects,
      isMultiSelect,
      unitOfMeasure,
      formatForDisplay,
      shapeManagerRef,
      setActiveObject,
      setActiveObjects,
      persistShapesFromManager,
      commitBatchOrPersist,
      useSecondPageOrigin,
      rightPageOriginX,
      rightPageOriginY,
      bleedWidth,
      bleedHeight,
      setPosition,
      setFieldErrors,
      setDisplayPrecision,
    ]
  )

  return { handlePositionChange, commitPosition }
}
